import {type PayPalNamespace, loadScript} from '@paypal/paypal-js';
import {disableGatewayOption, observeCheckoutHTML, onCheckoutGatewayChange} from '../checkout';
import {Feature} from '../features';
import {PayPalVenmoPayment} from './class-venmo-payment';
import {type AbstractPayPalPayment, type PayPalInstance} from './abstract/class-abstract-paypal-payment';
import {PayPalWalletPayment} from './class-paypal-wallet-payment';
import {PayPalPayLaterPayment} from './class-paypal-paylater-payment';
import {PayPalCreditPayment} from './class-paypal-credit-payment';
import {$qs} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {PayPalCardPayment} from './class-paypal-card-payment';
import {registerUpdatedCheckoutHandler, registerGatewaySelectedHandler, registerCheckoutSubmitHandler, createNativeCheckoutPaymentAttempt} from './checkout';
import {type PeachPayCheckoutData} from '../types';

declare const peachpay_checkout_data: PeachPayCheckoutData;

async function initPayPalPayments(): Promise<void> {
	if (!Feature.enabled(FeatureFlag.PAYPAL_GATEWAYS)) {
		return;
	}

	const clientId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'client_id');
	const merchantId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'merchant_id');
	const partnerAttributionId = Feature.metadata<string>(FeatureFlag.PAYPAL_GATEWAYS, 'partner_attribution_id');

	if (!clientId || !merchantId || !partnerAttributionId) {
		return;
	}

	const buttonPayments: AbstractPayPalPayment[] = [];
	if (Feature.enabled(FeatureFlag.PAYPAL_WALLET_GATEWAY)) {
		buttonPayments.push(new PayPalWalletPayment());
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_VENMO_GATEWAY)) {
		buttonPayments.push(new PayPalVenmoPayment());
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_PAYLATER_GATEWAY)) {
		buttonPayments.push(new PayPalPayLaterPayment());
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_CREDIT_GATEWAY)) {
		buttonPayments.push(new PayPalCreditPayment());
	}

	const hidePayPalButtons = () => {
		buttonPayments.forEach(p => {
			p.unmount();
		});
	};

	const showPayPalButton = (id: string) => {
		buttonPayments.forEach(p => {
			if (p.gatewayId() === id) {
				p.mount();
			}
		});
	};

	const hideWCButton = () => $qs('#place_order', $el => {
		$el.style.display = 'none';
	});
	const showWCButton = () => $qs('#place_order', $el => {
		$el.style.display = '';
	});

	// Hide or display currently selected PayPal method buttons.
	await onCheckoutGatewayChange((gatewayId: string) => {
		hidePayPalButtons();
		const payment = buttonPayments.find(m => m.gatewayId() === gatewayId);
		if (!payment) {
			showWCButton();
			return;
		}

		showPayPalButton(payment.gatewayId());
		hideWCButton();
	});

	const paypal = await loadScript({
		clientId,
		merchantId,
		dataPartnerAttributionId: partnerAttributionId,
		enableFunding: 'venmo',
		currency: peachpay_checkout_data.cart_details.currency,
		dataPageType: 'checkout',
		components: ['buttons', 'card-fields'],
	})
		.then((paypal: PayPalNamespace) => paypal as unknown as PayPalInstance)
		.catch((error: unknown) => {
			console.error('failed to load the PayPal JS SDK script', error);
			return null;
		});

	if (!paypal) {
		return;
	}

	if (Feature.enabled(FeatureFlag.PAYPAL_CARD_GATEWAY)) {
		try {
			const cardPayments = new PayPalCardPayment(paypal);
			if (cardPayments.isEligible()) {
				registerGatewaySelectedHandler(cardPayments.gatewayId(), () => {
					if (!cardPayments.isMounted()) {
						cardPayments.mount();
					}
				});

				registerUpdatedCheckoutHandler(cardPayments.gatewayId(), () => {
					if (!cardPayments.isMounted()) {
						cardPayments.mount();
					}
				});

				registerCheckoutSubmitHandler(cardPayments.gatewayId(), () => {
					cardPayments.submit(createNativeCheckoutPaymentAttempt());
				});
			} else {
				observeCheckoutHTML(disableGatewayOption(cardPayments.gatewayId()));
			}
		} catch (error: unknown) {
			console.error('PayPal Advanced cards: ', error);
		}
	}

	await Promise.all(buttonPayments.map(p => p.init(paypal)));
}

export {initPayPalPayments};
